import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import { apiDelete, apiGet, apiPatch, apiPost, formsEngineEndpoints, operationsClient } from '@/lib/api';
import { formsEngineKeys } from '@/lib/queryKeys';
import type {
  CreateFormTemplateInput,
  FormAssignment,
  FormStatus,
  FormSurface,
  FormTemplate,
  FormTemplateImpact,
  FormTemplateVersion,
  IntakePublicLink,
  UpdateFormTemplateInput,
  UpsertFormAssignmentInput,
} from '@/types/formsEngine';

export interface FormTemplateFilters {
  search?: string;
  surface?: FormSurface;
  status?: FormStatus;
}

function templateParams(filters: FormTemplateFilters) {
  return {
    ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.surface ? { surface: filters.surface } : {}),
    ...(filters.status ? { status: filters.status } : {}),
  };
}

export function useFormTemplates(filters: FormTemplateFilters = {}) {
  const params = templateParams(filters);
  return useApiQuery<FormTemplate[]>({
    queryKey: formsEngineKeys.templates(params),
    queryFn: () =>
      apiGet<FormTemplate[]>(operationsClient, formsEngineEndpoints.templates(), { params }),
    showErrorToast: false,
  });
}

export function useFormTemplate(id: string | undefined) {
  return useApiQuery<FormTemplate>({
    queryKey: formsEngineKeys.template(id ?? ''),
    queryFn: () =>
      apiGet<FormTemplate>(operationsClient, formsEngineEndpoints.template(id as string)),
    enabled: Boolean(id),
    showErrorToast: false,
  });
}

export function useFormTemplateVersions(id: string | undefined) {
  return useApiQuery<FormTemplateVersion[]>({
    queryKey: formsEngineKeys.templateVersions(id ?? ''),
    queryFn: () =>
      apiGet<FormTemplateVersion[]>(
        operationsClient,
        formsEngineEndpoints.templateVersions(id as string),
      ),
    enabled: Boolean(id),
    showErrorToast: false,
  });
}

interface RestoreVersionVariables {
  id: string;
  versionId: string;
}

/**
 * Publishes an earlier version's questions and settings again as a new
 * version. The template and its versions refetch, which reloads the builder
 * (its key carries the template version).
 */
export function useRestoreFormTemplateVersion() {
  const queryClient = useQueryClient();
  return useApiMutation<FormTemplate, RestoreVersionVariables>({
    mutationFn: ({ id, versionId }) =>
      apiPost<FormTemplate>(
        operationsClient,
        formsEngineEndpoints.restoreTemplateVersion(id, versionId),
      ),
    onSuccess: (_template, { id }) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.template(id) });
    },
  });
}

export function useFormTemplateImpact(id: string | undefined) {
  return useApiQuery<FormTemplateImpact>({
    queryKey: formsEngineKeys.templateImpact(id ?? ''),
    queryFn: () =>
      apiGet<FormTemplateImpact>(
        operationsClient,
        formsEngineEndpoints.templateImpact(id as string),
      ),
    enabled: Boolean(id),
    showErrorToast: false,
  });
}

function useTemplateStatusMutation(action: 'archive' | 'restore') {
  const queryClient = useQueryClient();
  return useApiMutation<FormTemplate, string>({
    mutationFn: (id) =>
      apiPost<FormTemplate>(
        operationsClient,
        action === 'archive'
          ? `${formsEngineEndpoints.template(id)}/archive`
          : `${formsEngineEndpoints.template(id)}/restore`,
      ),
    onSuccess: (_template, id) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.template(id) });
      toast.success(action === 'archive' ? 'Template archived' : 'Template restored');
    },
  });
}

export function useArchiveFormTemplate() {
  return useTemplateStatusMutation('archive');
}

export function useRestoreFormTemplate() {
  return useTemplateStatusMutation('restore');
}

interface UpsertAssignmentVariables extends UpsertFormAssignmentInput {
  templateId: string;
}

export function useUpsertFormTemplateAssignment() {
  const queryClient = useQueryClient();
  return useApiMutation<FormAssignment, UpsertAssignmentVariables>({
    mutationFn: ({ templateId, ...assignment }) =>
      apiPost<FormAssignment, UpsertFormAssignmentInput>(
        operationsClient,
        formsEngineEndpoints.templateAssignments(templateId),
        assignment,
      ),
    onSuccess: (_assignment, variables) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      void queryClient.invalidateQueries({
        queryKey: formsEngineKeys.template(variables.templateId),
      });
    },
  });
}

export function useDeleteFormTemplateAssignment() {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, { templateId: string; assignmentId: string }>({
    mutationFn: ({ templateId, assignmentId }) =>
      apiDelete(
        operationsClient,
        formsEngineEndpoints.templateAssignment(templateId, assignmentId),
      ),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      void queryClient.invalidateQueries({
        queryKey: formsEngineKeys.template(variables.templateId),
      });
    },
  });
}

interface CreateTemplateVariables {
  template: CreateFormTemplateInput;
  assignments?: UpsertFormAssignmentInput[];
}

interface UpdateTemplateVariables {
  id: string;
  template: UpdateFormTemplateInput;
  assignments?: UpsertFormAssignmentInput[];
}

export function useCreateFormTemplate() {
  const queryClient = useQueryClient();
  return useApiMutation<FormTemplate, CreateTemplateVariables>({
    mutationFn: async ({ template, assignments }) => {
      const created = await apiPost<
        FormTemplate,
        CreateFormTemplateInput & { assignments: UpsertFormAssignmentInput[] }
      >(operationsClient, formsEngineEndpoints.templates(), {
        ...template,
        assignments: assignments ?? [],
      });
      return apiGet<FormTemplate>(operationsClient, formsEngineEndpoints.template(created.id));
    },
    onSuccess: (template) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      toast.success(`${template.name} created`);
    },
  });
}

export function useUpdateAndPublishFormTemplate() {
  const queryClient = useQueryClient();
  return useApiMutation<FormTemplate, UpdateTemplateVariables>({
    mutationFn: async ({ id, template, assignments }) => {
      await apiPost<
        FormTemplate,
        UpdateFormTemplateInput & { assignments: UpsertFormAssignmentInput[] }
      >(operationsClient, formsEngineEndpoints.saveTemplate(id), {
        ...template,
        assignments: assignments ?? [],
      });
      return apiGet<FormTemplate>(operationsClient, formsEngineEndpoints.template(id));
    },
    onSuccess: (template) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      toast.success(`${template.name} updated`);
    },
  });
}

interface SetLeadSourceVariables {
  templateId: string;
  acquisitionChannelId: string;
}

/**
 * The owner picking which acquisition channel an INTAKE form's future
 * submissions report under (Task 3's `PATCH /forms/templates/:id/lead-source`).
 * Only ever applies going forward — submissions already recorded keep the
 * channel they resolved at the time.
 */
export function useSetFormTemplateLeadSource() {
  const queryClient = useQueryClient();
  return useApiMutation<FormTemplate, SetLeadSourceVariables>({
    mutationFn: ({ templateId, acquisitionChannelId }) =>
      apiPatch<FormTemplate, { acquisitionChannelId: string }>(
        operationsClient,
        formsEngineEndpoints.templateLeadSource(templateId),
        { acquisitionChannelId },
      ),
    onSuccess: (_template, variables) => {
      void queryClient.invalidateQueries({ queryKey: formsEngineKeys.all() });
      void queryClient.invalidateQueries({
        queryKey: formsEngineKeys.template(variables.templateId),
      });
      toast.success('Lead source updated');
    },
  });
}

/**
 * The permanent share key for an INTAKE form. The endpoint is an idempotent
 * get-or-create, so it is modelled as a query: opening the share dialog fetches
 * (and on first use, creates) the key, and reopening it reuses the cache.
 */
export function useIntakePublicLink(templateId: string, options: { enabled?: boolean } = {}) {
  return useApiQuery<IntakePublicLink>({
    queryKey: formsEngineKeys.intakePublicLink(templateId),
    queryFn: () =>
      apiPost<IntakePublicLink>(
        operationsClient,
        formsEngineEndpoints.intakePublicLink(templateId),
      ),
    enabled: Boolean(templateId) && (options.enabled ?? true),
    staleTime: Infinity,
    retry: false,
    showErrorToast: false,
  });
}

/** Replaces the intake form's share key; the previous key stops resolving. */
export function useRegenerateIntakePublicLink(templateId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<IntakePublicLink, void>({
    mutationFn: () =>
      apiPost<IntakePublicLink>(
        operationsClient,
        formsEngineEndpoints.regenerateIntakePublicLink(templateId),
      ),
    onSuccess: (link) => {
      queryClient.setQueryData(formsEngineKeys.intakePublicLink(templateId), link);
      toast.success('New link created. The previous link no longer works.');
    },
  });
}
